import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, Card, Badge, Table, Avatar } from '@/components/ui';
import { useConstructionCosts } from './hooks/useConstructionCost';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import type { CostType, CostStatusCode } from './types/construction-cost.types';

const typeColors: Record<CostType, string> = {
  labor: 'bg-blue-100 text-blue-600',
  material: 'bg-emerald-100 text-emerald-600',
  equipment: 'bg-amber-100 text-amber-600',
  subcontractor: 'bg-indigo-100 text-indigo-600',
  transport: 'bg-slate-100 text-slate-600',
  overhead: 'bg-slate-100 text-slate-600',
  rework: 'bg-red-100 text-red-600',
  variation: 'bg-orange-100 text-orange-600',
  other: 'bg-slate-100 text-slate-500',
};

const statusColors: Record<CostStatusCode, string> = {
  draft: 'bg-slate-100 text-slate-500',
  pending: 'bg-amber-100 text-amber-600',
  reviewed: 'bg-blue-100 text-blue-600',
  approved: 'bg-emerald-500 text-white',
  rejected: 'bg-red-500 text-white',
  paid: 'bg-indigo-500 text-white',
  locked: 'bg-slate-900 text-white',
};

export default function CostTrackingPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const navigate = useNavigate();

  const {
    costs,
    summary,
    isLoading,
    filters,
    setFilters,
  } = useConstructionCosts(companyId);

  return (
    <PageLayout
      title="Theo dõi Chi phí Thi công"
      description="Quản lý thực chi dự án, đối soát ngân sách BOQ và kiểm soát phát sinh từ hiện trường."
      breadcrumbs={[{ label: 'Thi công' }, { label: 'Chi phí' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-file-excel-2-line">Xuất báo cáo</Button>
          <AuthButton
            module="payments"
            action="create"
            variant="primary"
            icon="ri-add-line"
            onClick={() => navigate('./create')}
          >
            Ghi nhận chi phí
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* KPI Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Thực chi (Actual)"
            value={summary?.totalActual.toLocaleString() || 0}
            icon="ri-money-dollar-circle-line"
            variant="primary"
            suffix=" VNĐ"
          />
          <KPICard
            title="Chênh lệch (Variance)"
            value={summary?.variance.toLocaleString() || 0}
            icon="ri-scales-3-line"
            variant={summary?.variance && summary.variance < 0 ? 'danger' : 'success'}
            suffix=" VNĐ"
          />
          <KPICard
            title="Chi phí Vật tư"
            value={summary?.materialCost.toLocaleString() || 0}
            icon="ri-stack-line"
            variant="info"
          />
          <KPICard
            title="Chi phí Rework"
            value={summary?.reworkCost.toLocaleString() || 0}
            icon="ri-error-warning-line"
            variant="warning"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-col md:flex-row gap-4 w-full justify-between items-start md:items-center">
            <div className="flex flex-wrap gap-3 flex-1">
              <Search
                placeholder="Tìm nội dung, mã nguồn..."
                value={filters.search}
                onChange={(v) => setFilters({ ...filters, search: v })}
                className="w-full md:w-80"
              />
              <Filter
                options={[
                  {
                    id: 'type', label: 'Loại chi phí', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả loại' },
                      { value: 'labor', label: 'Nhân công' },
                      { value: 'material', label: 'Vật tư' },
                      { value: 'equipment', label: 'Máy thi công' },
                      { value: 'rework', label: 'Chi phí Rework' },
                      { value: 'variation', label: 'Phát sinh (VO)' }
                    ]
                  },
                  {
                    id: 'status', label: 'Trạng thái', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả trạng thái' },
                      { value: 'pending', label: 'Chờ duyệt' },
                      { value: 'approved', label: 'Đã duyệt' },
                      { value: 'locked', label: 'Đã khóa sổ' }
                    ]
                  }
                ]}
                onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
              />
            </div>
          </div>
        </PageLayout.Toolbar>

        <Card className="overflow-hidden border-none shadow-sm">
          <Table
            columns={[
              { key: 'date', title: 'Ngày' },
              { key: 'description', title: 'Nội dung chi phí' },
              { key: 'type', title: 'Loại', align: 'center' },
              { key: 'amount', title: 'Giá trị (VNĐ)', align: 'right' },
              { key: 'recorder', title: 'Người ghi' },
              { key: 'status', title: 'Trạng thái', align: 'center' },
            ]}
            data={costs}
            isLoading={isLoading}
            onRowClick={(item) => navigate(`./detail/${item.id}`)}
            renderCell={(item, key) => {
              switch (key) {
                case 'date':
                  return <span className="text-[11px] font-medium text-slate-500 italic">{new Date(item.cost_date).toLocaleDateString('vi-VN')}</span>;
                case 'description':
                  return (
                    <div className="flex flex-col">
                      <span className="font-bold text-slate-900 text-xs">{item.description}</span>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] text-slate-400 font-bold  tracking-tighter">{item.source_type}</span>
                        <span className="text-slate-300">•</span>
                        <span className="text-[10px] text-os-primary italic font-mono">{item.source_id}</span>
                      </div>
                    </div>
                  );
                case 'type':
                  return <Badge className={`${typeColors[item.cost_type]} border-transparent text-[9px] font-bold `}>{item.cost_type}</Badge>;
                case 'amount':
                  return (
                    <div className="flex flex-col items-end">
                      <span className="text-sm font-bold text-slate-900">{item.actual_amount.toLocaleString()}</span>
                      <span className="text-[9px] text-slate-400 italic line-through">{item.planned_amount?.toLocaleString()}</span>
                    </div>
                  );
                case 'recorder':
                  return (
                    <div className="flex items-center gap-2">
                      <Avatar name={item.recorder?.full_name} size="xs" src={item.recorder?.avatar_url} />
                      <span className="text-[10px] font-medium text-slate-600">{item.recorder?.full_name}</span>
                    </div>
                  );
                case 'status':
                  return (
                    <Badge className={`${statusColors[item.status_code]} border-transparent text-[9px] font-bold `}>
                      {item.status_code}
                    </Badge>
                  );
                default:
                  return null;
              }
            }}
          />
        </Card>
      </div>
    </PageLayout>
  );
}
